import type { Viewport } from '../../../engine/hex/camera2d';
import type { HexMapState } from '../../../engine/render/renderer';
import {
    ACTION__SIM_SPEED__FIVE,
    ACTION__SIM_SPEED__ONE,
    ACTION__SIM_SPEED__START,
    ACTION__SIM_SPEED__STOP,
    ACTION__SIM_SPEED__TEN,
    ACTION__SIM_SPEED__TWO,
    ACTION__UI__LANGUAGE__EN,
    ACTION__UI__LANGUAGE__PL,
    ACTION__UI__OPEN_SETTINGS,
    ACTION__UI__TOGGLE_NOTIFICATIONS,
    ACTION__UI__TOGGLE_SPEED_MENU,
    ACTION__UI__TOGGLE_LANGUAGE_MENU,
} from '../../../engine/input/actionIds';
import { t } from '../../../i18n/translations';
import type { IconRegistry } from '../assets/IconRegistry';
import { overlayTokens } from '../styles/tokens';
import type { ActionBarVM, ActionButtonVM, ActionMenuVM } from './types';
import { buildAlertDismissibles, buildNotificationDismissibles } from './dismissibles';
import { isMobileViewport } from '../../../engine/render/modalLayout';

export type TextMeasurer = (text: string, font: string) => number;

type BuildActionBarArgs = {
    state: HexMapState;
    viewport: Viewport;
    icons: IconRegistry;
    measureText: TextMeasurer;
};

export function buildActionBarVM({
    state,
    viewport,
    icons,
    measureText,
}: BuildActionBarArgs): ActionBarVM {
    const lang = state.config.ui.language;
    const badgeColor = getNotificationBadgeColor(state);
    const mobile = isMobileViewport(viewport);
    const actions: Array<Omit<ActionButtonVM, 'rect'>> = [
        {
            id: ACTION__UI__OPEN_SETTINGS,
            label: '',
            enabled: true,
            active: false,
            iconId: 'settings',
            iconReady: icons.isReady('settings'),
        },
        {
            id: ACTION__UI__TOGGLE_NOTIFICATIONS,
            label: '🔔',
            enabled: true,
            active: state.overlay.showNotifications,
            badgeColor,
        },
        ...(mobile
            ? [
                  {
                      id: ACTION__UI__TOGGLE_SPEED_MENU,
                      label: `⏱ ${state.simulation.speed}x`,
                      enabled: true,
                      active: state.overlay.speedMenuOpen,
                  },
                  {
                      id: ACTION__UI__TOGGLE_LANGUAGE_MENU,
                      label: `🌐 ${state.config.ui.language.toUpperCase()}`,
                      enabled: true,
                      active: state.overlay.languageMenuOpen,
                  },
              ]
            : []),
        {
            id: ACTION__SIM_SPEED__STOP,
            label: t(lang, 'overlay.stop'),
            enabled: state.simulation.running,
            active: false,
        },
        {
            id: ACTION__SIM_SPEED__START,
            label: t(lang, 'overlay.start'),
            enabled: !state.simulation.running,
            active: false,
        },
        ...(mobile
            ? []
            : [
                  {
                      id: ACTION__SIM_SPEED__ONE,
                      label: t(lang, 'overlay.speed1'),
                      enabled: true,
                      active: state.simulation.speed === 1,
                  },
                  {
                      id: ACTION__SIM_SPEED__TWO,
                      label: t(lang, 'overlay.speed2'),
                      enabled: true,
                      active: state.simulation.speed === 2,
                  },
                  {
                      id: ACTION__SIM_SPEED__FIVE,
                      label: t(lang, 'overlay.speed5'),
                      enabled: true,
                      active: state.simulation.speed === 5,
                  },
                  {
                      id: ACTION__SIM_SPEED__TEN,
                      label: t(lang, 'overlay.speed10'),
                      enabled: true,
                      active: state.simulation.speed === 100,
                  },
              ]),
        ...(mobile
            ? []
            : [
                  {
                      id: ACTION__UI__LANGUAGE__EN,
                      label: t(lang, 'lang.en'),
                      enabled: true,
                      active: state.config.ui.language === 'en',
                  },
                  {
                      id: ACTION__UI__LANGUAGE__PL,
                      label: t(lang, 'lang.pl'),
                      enabled: true,
                      active: state.config.ui.language === 'pl',
                  },
              ]),
    ];

    const { barPadding, buttonPadX, buttonHeight, buttonGap, barY } = overlayTokens.layout;
    const buttonWidths = actions.map((action) => {
        const labelWidth = action.label ? measureText(action.label, overlayTokens.fonts.base) : 0;
        const base = Math.max(26, labelWidth + buttonPadX * 2);
        return action.iconId ? Math.max(base, buttonHeight) : base;
    });

    const totalButtons =
        buttonWidths.reduce((sum, width) => sum + width, 0) + buttonGap * (buttonWidths.length - 1);
    const barW = totalButtons + barPadding * 2;
    const barH = buttonHeight + barPadding * 2;
    const barX = Math.max(12, viewport.width - barW - 12);

    const buttons: ActionButtonVM[] = [];
    let cx = barX + barPadding;
    const cy = barY + barPadding;
    actions.forEach((action, index) => {
        const width = buttonWidths[index];
        buttons.push({
            ...action,
            rect: { x: cx, y: cy, w: width, h: buttonHeight },
        });
        cx += width + buttonGap;
    });

    return {
        rect: { x: barX, y: barY, w: barW, h: barH },
        buttons,
    };
}

export function buildActionMenus(
    state: HexMapState,
    viewport: Viewport,
    measureText: TextMeasurer,
    anchorRect: { x: number; y: number; w: number; h: number },
): ActionMenuVM[] {
    const lang = state.config.ui.language;
    const menus: ActionMenuVM[] = [];
    if (!isMobileViewport(viewport)) return menus;
    const baseY = anchorRect.y + anchorRect.h + 6;
    const menuPadding = 6;
    const buttonGap = 6;
    const buttonHeight = overlayTokens.layout.buttonHeight;

    if (state.overlay.speedMenuOpen) {
        const items: Array<Omit<ActionButtonVM, 'rect'>> = [
            {
                id: ACTION__SIM_SPEED__ONE,
                label: t(lang, 'overlay.speed1'),
                enabled: true,
                active: state.simulation.speed === 1,
            },
            {
                id: ACTION__SIM_SPEED__TWO,
                label: t(lang, 'overlay.speed2'),
                enabled: true,
                active: state.simulation.speed === 2,
            },
            {
                id: ACTION__SIM_SPEED__FIVE,
                label: t(lang, 'overlay.speed5'),
                enabled: true,
                active: state.simulation.speed === 5,
            },
            {
                id: ACTION__SIM_SPEED__TEN,
                label: t(lang, 'overlay.speed10'),
                enabled: true,
                active: state.simulation.speed === 100,
            },
        ];
        const menu = layoutMenu(
            items,
            viewport,
            measureText,
            baseY,
            menuPadding,
            buttonGap,
            buttonHeight,
        );
        menus.push(menu);
    }

    if (state.overlay.languageMenuOpen) {
        const items: Array<Omit<ActionButtonVM, 'rect'>> = [
            {
                id: ACTION__UI__LANGUAGE__EN,
                label: t(lang, 'lang.en'),
                enabled: true,
                active: state.config.ui.language === 'en',
            },
            {
                id: ACTION__UI__LANGUAGE__PL,
                label: t(lang, 'lang.pl'),
                enabled: true,
                active: state.config.ui.language === 'pl',
            },
        ];
        const offsetY = baseY + (menus[0]?.rect.h ?? 0) + 6;
        const menu = layoutMenu(
            items,
            viewport,
            measureText,
            offsetY,
            menuPadding,
            buttonGap,
            buttonHeight,
        );
        menus.push(menu);
    }

    return menus;
}

function layoutMenu(
    items: Array<Omit<ActionButtonVM, 'rect'>>,
    viewport: Viewport,
    measureText: TextMeasurer,
    y: number,
    padding: number,
    gap: number,
    height: number,
): ActionMenuVM {
    const widths = items.map((item) => {
        const labelWidth = item.label ? measureText(item.label, overlayTokens.fonts.base) : 0;
        return Math.max(26, labelWidth + overlayTokens.layout.buttonPadX * 2);
    });
    const totalWidth = widths.reduce((sum, width) => sum + width, 0) + gap * (widths.length - 1);
    const menuW = Math.min(viewport.width - 16, totalWidth + padding * 2);
    const menuX = Math.max(8, viewport.width - menuW - 8);
    const rect = { x: menuX, y, w: menuW, h: height + padding * 2 };
    let cx = rect.x + padding;
    const cy = rect.y + padding;
    const buttons = items.map((item, index) => {
        const w = widths[index];
        const button = { ...item, rect: { x: cx, y: cy, w, h: height } };
        cx += w + gap;
        return button;
    });
    return { rect, buttons };
}

function getNotificationBadgeColor(state: HexMapState): string | null {
    const lang = state.config.ui.language;
    const notifications = buildNotificationDismissibles(state, lang).filter(
        (notification) => !state.dismissedNotifications.has(notification.id),
    );
    const alerts = buildAlertDismissibles(state);
    if (alerts.some((alert) => alert.severity === 'error')) {
        return overlayTokens.colors.badgeError;
    }
    if (alerts.length > 0 || notifications.length > 0) {
        return overlayTokens.colors.badgeWarn;
    }
    return null;
}
